import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { ApiCallError } from '@/api/client';
import { useIntuneForRmmDevice } from '@/api/cipp';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { cippDevicesView, cippTenantPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * Intune card for the RMM device page — the reverse of the CIPP devices page's
 * "Datto RMM" column. Matched server-side by serial number (hostname fallback within
 * the same client). Hidden entirely when CIPP isn't configured.
 */

const COMPLIANCE: Record<string, { label: string; cls: string }> = {
  compliant: { label: 'Compliant', cls: 'bg-success-soft text-success' },
  noncompliant: { label: 'Non-compliant', cls: 'bg-destructive/10 text-destructive' },
  inGracePeriod: { label: 'Grace period', cls: 'bg-warning-soft text-warning' },
};

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-t border-divider px-4 py-2.5 first:border-t-0">
      <span className="shrink-0 pt-[2px] text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="min-w-0 text-right text-[13px] text-foreground">{children}</div>
    </div>
  );
}

export function IntuneDeviceCard({ uid }: { uid: string }) {
  const q = useIntuneForRmmDevice(uid);

  if (q.error instanceof ApiCallError && q.error.payload?.error?.code === 'CIPP_NOT_CONFIGURED') return null;

  const header = <div className="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Intune</div>;

  if (q.isLoading) {
    return (
      <section className="rounded-lg border border-border bg-card shadow-sm">
        {header}
        <div className="flex items-center gap-2 px-4 pb-3 pt-1 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking Intune…
        </div>
      </section>
    );
  }

  const d = q.data?.items[0];
  if (q.error || !d) {
    return (
      <section className="rounded-lg border border-border bg-card shadow-sm">
        {header}
        <div className="px-4 pb-3 pt-1 text-xs text-muted-foreground">
          {q.error ? "Couldn't reach CIPP." : 'Not found in Intune (no device with this serial number).'}
        </div>
      </section>
    );
  }

  const comp = (d.compliance ? COMPLIANCE[d.compliance] : undefined) ?? { label: d.compliance ?? 'Unknown', cls: 'bg-muted text-muted-foreground' };

  return (
    <section className="rounded-lg border border-border bg-card shadow-sm">
      {header}
      <Row label="Compliance"><span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', comp.cls)}>{comp.label}</span></Row>
      <Row label="User">
        {d.userDisplayName ?? d.userPrincipalName ?? '—'}
        {d.userDisplayName && d.userPrincipalName && <div className="text-xs text-muted-foreground">{d.userPrincipalName}</div>}
      </Row>
      <Row label="Last sync">{d.lastSync ? <span title={new Date(d.lastSync).toLocaleString()}>{fmtRelative(d.lastSync)}</span> : 'Never'}</Row>
      <Row label="Enrolled">
        {d.enrolled ? fmtRelative(d.enrolled) : '—'}
        <div className="text-xs text-muted-foreground">
          {[d.ownership === 'personal' ? 'Personal' : d.ownership === 'company' ? 'Corporate' : null, d.autopilot ? 'Autopilot' : null].filter(Boolean).join(' · ')}
        </div>
      </Row>
      {d.encrypted != null && <Row label="Encrypted">{d.encrypted ? 'Yes' : <span className="text-warning">No</span>}</Row>}
      <Row label="Tenant"><Link to={cippTenantPath(d.tenant.customerId)} className="hover:underline">{d.companyName ?? d.tenant.displayName}</Link></Row>
      <div className="border-t border-divider px-4 py-2 text-[11px] text-muted-foreground">
        {d.rmm?.matchedBy === 'hostname' ? 'Matched by device name' : d.rmm?.matchedBy === 'mac' ? 'Matched by network card (MAC)' : 'Matched by serial number'}
        {d.rmm?.companyMismatch && <span className="text-warning"> · tenant is a different client</span>}
        {' · '}
        <Link to={cippDevicesView({ q: d.serialNumber ?? d.deviceName })} className="underline-offset-2 hover:text-foreground hover:underline">View in Intune devices</Link>
      </div>
    </section>
  );
}
